import { useTranslation } from "react-i18next";
import BtnCustom from "components/common/btn-custom";
import CustomTable from "components/ReactTable/CustomTable";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "reactstrap";
import { ErrorToaster, successToaster } from "components/common/toast";
import handleError from "utils/handleError";
import ReactBSAlert from "react-bootstrap-sweetalert";
import TableFilter from "components/TableFilter/TableFilter";
import { GetBeverages, DeleteBeverage } from "network/beverages";
import { BEVERAGE_SECTIONS, sectionLabel } from "constant/beverages";

export default function BeverageTable() {
  const { t } = useTranslation("beverages");
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState([]);
  const [section, setSection] = useState("beverages");
  const [fetchTrigger, setFetchTrigger] = useState(false);

  const pageSize = 10;

  const hideAlert = () => setAlert(null);

  const handleDelete = async (id) => {
    try {
      const res = await DeleteBeverage(id);
      if (res.success) {
        successToaster(t("table.deleted"));
        setItems((prev) => prev.filter((value) => value?.uuid !== id));
      }
    } catch (error) {
      ErrorToaster(handleError(error?.status_code, error?.message));
    }
    hideAlert();
  };

  const askDelete = (id) => {
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title={t("table.confirmTitle")}
        onConfirm={() => handleDelete(id)}
        onCancel={hideAlert}
        confirmBtnBsStyle="success"
        cancelBtnBsStyle="danger"
        confirmBtnText={t("table.confirmYes")}
        cancelBtnText={t("table.cancel")}
        showCancel
        btnSize=""
      >
        {t("table.confirmText")}
      </ReactBSAlert>
    );
  };

  const columns = useMemo(
    () => [
      { Header: t("table.colEan"), accessor: "ean", Cell: ({ value }) => value || "-" },
      { Header: t("table.colName"), accessor: "name", Cell: ({ value }) => value || "-" },
      { Header: t("table.colBrand"), accessor: "brand", Cell: ({ value }) => value || "-" },
      {
        Header: t("table.colCategory"),
        accessor: "section",
        Cell: ({ value }) => sectionLabel(value),
      },
      {
        Header: t("table.colStatus"),
        accessor: "is_active",
        Cell: ({ value }) => (value ? t("table.active") : t("table.inactive")),
      },
      {
        Header: t("table.colActions"),
        accessor: "Action",
        Cell: ({ row }) => (
          <div className="d-flex align-items-center">
            <i
              className="tim-icons icon-pencil mr-3 text-warning pointer"
              onClick={() => navigate(`/beverages/edit/${row?.original?.uuid}`)}
            ></i>
            <i
              className="tim-icons icon-trash-simple text-danger pointer"
              onClick={() => askDelete(row?.original?.uuid)}
            ></i>
          </div>
        ),
      },
    ],
    [t]
  );

  async function getData() {
    try {
      setIsLoading(true);
      const response = await GetBeverages(
        currentPage,
        pageSize,
        section,
        search,
        sort?.value
      );
      if (response.success) {
        setItems(response?.data?.beverages || []);
        setTotalPages(response?.data?.total_pages || 1);
      }
    } catch (errors) {
      ErrorToaster(handleError(errors?.status_code, errors?.message));
    }
    setIsLoading(false);
  }

  useEffect(() => {
    setCurrentPage(1);
    setFetchTrigger((prev) => !prev);
  }, [sort, search, section]);

  useEffect(() => {
    getData();
  }, [currentPage, fetchTrigger]);

  return (
    <div className="content my-4">
      <Card className="p-4">
        {alert}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
          <h2 className="card-title">{t("table.title")}</h2>
          <BtnCustom
            color="btn btn-primary"
            onClick={() => navigate(`/beverages/add?section=${section}`)}
          >
            {t("table.new")}
          </BtnCustom>
        </div>

        <div className="d-flex flex-wrap mb-3">
          {BEVERAGE_SECTIONS.map((s) => (
            <button
              key={s.code}
              type="button"
              className={`btn btn-sm mr-2 ${
                section === s.code ? "btn-primary" : "btn-secondary"
              }`}
              onClick={() => setSection(s.code)}
            >
              {s.label}
            </button>
          ))}
        </div>

        <TableFilter
          setSearch={setSearch}
          search={search}
          showSearch
          showSort
          setSort={setSort}
          sort={sort}
        />
        <CustomTable
          data={items}
          columns={columns}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          showPagination={totalPages > 1}
          isLoading={isLoading}
        />
      </Card>
    </div>
  );
}
